import type { ReactNode } from "react";
import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useCarLocationAutocomplete } from "@tenant/cars";
import {
  Icon,
  LocationAutocomplete,
  type LocationAutocompleteProps,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarLocationAutocomplete.module.scss";
import { fetchLocations } from "./fetchLocations";
import type { CarLocationSuggestion } from "./types";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type CarLocationAutocompleteProps = Omit<
  LocationAutocompleteProps<CarLocationSuggestion>,
  "fetch" | "groupBy" | "getOptionLabel" | "sortOptions"
> & {
  value?: CarLocationSuggestion;
  topContent?: ReactNode;
  useDialogOnMobile?: boolean;
} & PlatformComponentProps;

const COMPONENT_KEY = CoreCarsComponent.CarLocationAutocomplete;

export const CarLocationAutocomplete = (
  props: CarLocationAutocompleteProps
) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { className } = props;
  const { context, handlers } = useCarLocationAutocomplete(props);

  return (
    <div className={clsx(block, className)}>
      <LocationAutocomplete<CarLocationSuggestion>
        id={context.id}
        value={context.value}
        label={context.label}
        onChange={handlers.onChange}
        fetch={fetchLocations}
        getOptionLabel={getOptionLabel}
        className={context.className}
        paperClassName={cn("location-paper")}
        popperClassName={cn("location-popper")}
        onOpen={handlers.onOpen}
        onFocus={handlers.onFocus}
        placeholder={context.placeholder}
        getOptionIcon={getOptionIcon}
        useDialogOnMobile={context.useDialogOnMobile}
        topContent={props.topContent}
        groupBy={handlers.groupBy}
        getGroupLabelKey={handlers.getGroupLabelKey}
        error={context.error}
      />
    </div>
  );
};

const getOptionLabel = (value?: CarLocationSuggestion) => {
  if (value != null) {
    return value.label;
  }
  return "";
};

const getOptionIcon = (value?: CarLocationSuggestion) => {
  return <Icon iconName={IconNameEnum.mapPin} />;
};
